export type Mode = "entering" | "browsing" | "opening" | "open" | "closing"

export interface Retailer {
    name: string
    price: string
    url: string
}

export interface BookData {
    id: string
    title: string
    author: string
    spineColor: string
    spineTextColor: string
    coverColor: string
    coverTextColor: string
    coverImage?: string
    synopsis: string
    excerpt: string
    retailers: Retailer[]
    width: number
    height: number
    spineThickness: number
}

// ---------------------------------------------------------------------------
// Shelf + animation constants (world units / seconds)
// ---------------------------------------------------------------------------

// Shelf layout
export const SHELF_PITCH = 0.82 // base spacing between spine-mode books
export const COVER_PADDING = 0.85 // extra width added to centred slot
export const COVER_Z_LIFT = 0.18 // forward shift for centred (cover-forward) book
export const SHELF_Y = -3.1 // top of shelf in world Y (book bottoms rest here)

// Open / close target
export const OPEN_POS_X = -1.25
export const OPEN_POS_Y = -1.5 // book BOTTOM in world
export const OPEN_POS_Z = 2.4
export const OPEN_ANGLE = Math.PI * 0.8 // 144deg front-cover swing
export const OPEN_TILT_X = -0.26 // backward tilt (rad)

export const OPEN_DURATION = 1.85
export const CLOSE_DURATION = 1.0

// Other-books retreat during open
export const FALL_DEPTH = 3.2
export const SHELF_OPEN_Z = 4.5
export const SHELF_OPEN_Y = -5.5

// Slider physics
export const SLIDER_APPROACH = 0.16

// Wheel / drag
export const WHEEL_PX_PER_SLIDE = 240
export const SETTLE_IDLE_MS = 220
export const DRAG_THRESHOLD_PX = 36

// Entrance
export const ENTRANCE_DURATION = 2.0
export const ENTRANCE_STAGGER = 0.1
export const ENTRY_FROM_X = 16
export const ENTRY_FROM_Y_OFFSET = 0.4
export const ENTRY_FROM_Z = 0.1

// Book mesh
export const COVER_THICK = 0.05
export const SPINE_WRAP_T = 0.06
export const PAGE_INSET = 0.025

// Mobile open pose
export const MOBILE_OPEN_POS_X = 0.8
export const MOBILE_OPEN_POS_Y = -0.25
export const MOBILE_OPEN_POS_Z = -1.5

// Background images (downloaded into /public/images)
export const BG_IMAGE_DARK = "/images/books-bg.png"
export const BG_IMAGE_LIGHT = "/images/books-bg-day.png"

// ---------------------------------------------------------------------------
// Math helpers (pure, SSR-safe)
// ---------------------------------------------------------------------------

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t
export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
export const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x)
export const modN = (i: number, n: number) => ((i % n) + n) % n

export const smoothstep = (t: number, a: number, b: number) => {
    const x = clamp01((t - a) / (b - a))
    return x * x * (3 - 2 * x)
}

export const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2
export const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4)
export const easeInOutCubic = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

// ---------------------------------------------------------------------------
// Shelf layout — continuous "coverness"
// ---------------------------------------------------------------------------

export interface ShelfLayout {
    centres: number[]
    covernesses: number[]
    anchor: number
}

export function layoutShelf(position: number): ShelfLayout {
    const N = BOOKS.length
    const widths: number[] = []
    const cov: number[] = []

    for (let i = 0; i < N; i++) {
        const relP = i - position
        const c = Math.max(0, 1 - Math.abs(relP))
        cov.push(c)
        const expanded = BOOKS[i].width + COVER_PADDING
        widths.push(SHELF_PITCH + (expanded - SHELF_PITCH) * c)
    }

    const lefts: number[] = [0]
    for (let i = 1; i < N; i++) lefts.push(lefts[i - 1] + widths[i - 1])
    const centres = widths.map((w, i) => lefts[i] + w / 2)

    const lo = Math.floor(position)
    const hi = lo + 1
    const frac = position - lo
    const safeLo = Math.max(0, Math.min(N - 1, lo))
    const safeHi = Math.max(0, Math.min(N - 1, hi))
    const anchor = lerp(centres[safeLo], centres[safeHi], frac)

    return { centres, covernesses: cov, anchor }
}

// ---------------------------------------------------------------------------
// Retailer link helpers — search URLs always resolve to a live page
// ---------------------------------------------------------------------------

const amz = (q: string) => `https://www.amazon.com/s?k=${q.replace(/ /g, "+")}`
const bsp = (q: string) => `https://bookshop.org/search?keywords=${q.replace(/ /g, "+")}`

const shops = (q: string, amazonPrice: string, bookshopPrice: string): Retailer[] => [
    { name: "Amazon", price: amazonPrice, url: amz(q) },
    { name: "Bookshop", price: bookshopPrice, url: bsp(q) }
]

// ---------------------------------------------------------------------------
// The 12-book curation
// ---------------------------------------------------------------------------

export const BOOKS: BookData[] = [
    {
        id: "zero-to-one",
        title: "Zero to One",
        author: "Peter Thiel",
        spineColor: "#0d0d0d",
        spineTextColor: "#f3ecd8",
        coverColor: "#0d0d0d",
        coverTextColor: "#f3ecd8",
        synopsis:
            "Notes on startups, or how to build the future. Peter Thiel's Stanford lectures distilled into a manual for monopoly thinking, going from zero to one, not one to n.",
        excerpt: "What important truth do very few people agree with you on?",
        retailers: shops("Zero to One Peter Thiel", "$15.99", "$16.74"),
        width: 1.95,
        height: 3.05,
        spineThickness: 0.34
    },
    {
        id: "lean-startup",
        title: "The Lean Startup",
        author: "Eric Ries",
        spineColor: "#274034",
        spineTextColor: "#f3ecd8",
        coverColor: "#fbf6e8",
        coverTextColor: "#1a1a1a",
        synopsis:
            "How today's entrepreneurs use continuous innovation to create radically successful businesses. The build-measure-learn loop, MVPs, and pivots, the playbook a generation grew up on.",
        excerpt: "The only way to win is to learn faster than anyone else.",
        retailers: shops("The Lean Startup Eric Ries", "$17.49", "$18.59"),
        width: 2.0,
        height: 3.15,
        spineThickness: 0.42
    },
    {
        id: "good-to-great",
        title: "Good to Great",
        author: "Jim Collins",
        spineColor: "#6d1f1f",
        spineTextColor: "#f3ecd8",
        coverColor: "#f5ede0",
        coverTextColor: "#6d1f1f",
        synopsis:
            "Why some companies make the leap and others don't. A five-year study identifies the disciplines, Level 5 leaders, the hedgehog concept, the flywheel, that turn good companies into enduring great ones.",
        excerpt: "Good is the enemy of great.",
        retailers: shops("Good to Great Jim Collins", "$19.99", "$24.18"),
        width: 2.05,
        height: 3.2,
        spineThickness: 0.46
    },
    {
        id: "atomic-habits",
        title: "Atomic Habits",
        author: "James Clear",
        spineColor: "#cdb55b",
        spineTextColor: "#1a1a1a",
        coverColor: "#fbf6e8",
        coverTextColor: "#1a1a1a",
        synopsis:
            "Tiny changes, remarkable results. A practical, evidence-based framework for compounding small habits into outsized outcomes, built around four laws that shape every behaviour you'll ever form.",
        excerpt:
            "You do not rise to the level of your goals. You fall to the level of your systems.",
        retailers: shops("Atomic Habits James Clear", "$14.99", "$25.11"),
        width: 2.0,
        height: 3.0,
        spineThickness: 0.4
    },
    {
        id: "shoe-dog",
        title: "Shoe Dog",
        author: "Phil Knight",
        spineColor: "#a8501f",
        spineTextColor: "#f3ecd8",
        coverColor: "#0d0d0d",
        coverTextColor: "#f3ecd8",
        synopsis:
            "A memoir by the creator of Nike. From a $50 loan from his father to one of the world's most iconic brands.",
        excerpt:
            "Don't tell people how to do things, tell them what to do and let them surprise you.",
        retailers: shops("Shoe Dog Phil Knight", "$18.00", "$19.53"),
        width: 1.95,
        height: 3.0,
        spineThickness: 0.38
    },
    {
        id: "hard-thing",
        title: "The Hard Thing About Hard Things",
        author: "Ben Horowitz",
        spineColor: "#1a1a1a",
        spineTextColor: "#cf9b3a",
        coverColor: "#1a1a1a",
        coverTextColor: "#cf9b3a",
        synopsis:
            "Building a business when there are no easy answers. Ben Horowitz on layoffs, demotions, betrayals, and the crushing weight of decisions no MBA prepares you for.",
        excerpt:
            "There is no recipe for the hard things. There is no recipe for really complex situations.",
        retailers: shops("The Hard Thing About Hard Things Ben Horowitz", "$21.99", "$27.90"),
        width: 2.0,
        height: 3.1,
        spineThickness: 0.42
    },
    {
        id: "four-hour-week",
        title: "The 4-Hour Workweek",
        author: "Tim Ferriss",
        spineColor: "#b8932a",
        spineTextColor: "#1a1a1a",
        coverColor: "#fbf6e8",
        coverTextColor: "#1a1a1a",
        synopsis:
            "Escape 9 to 5, live anywhere, and join the new rich. Tim Ferriss's manifesto for trading time-for-money for systems-for-freedom.",
        excerpt: "What we fear doing most is usually what we most need to do.",
        retailers: shops("The 4-Hour Workweek Tim Ferriss", "$22.00", "$20.46"),
        width: 2.0,
        height: 3.05,
        spineThickness: 0.44
    },
    {
        id: "principles",
        title: "Principles",
        author: "Ray Dalio",
        spineColor: "#1c2a44",
        spineTextColor: "#f3ecd8",
        coverColor: "#f3ecd8",
        coverTextColor: "#1c2a44",
        synopsis:
            "Life and work principles from the founder of Bridgewater. Radical transparency, idea meritocracy, and an algorithmic approach to decision-making.",
        excerpt: "Pain plus reflection equals progress.",
        retailers: shops("Principles Ray Dalio", "$30.00", "$27.90"),
        width: 2.05,
        height: 3.3,
        spineThickness: 0.58
    },
    {
        id: "rework",
        title: "Rework",
        author: "Jason Fried",
        spineColor: "#f0e7d0",
        spineTextColor: "#1a1a1a",
        coverColor: "#f0e7d0",
        coverTextColor: "#1a1a1a",
        synopsis:
            "Change the way you work forever. Short, blunt essays on the nonsense of business orthodoxy.",
        excerpt: "Planning is guessing. Workaholism is stupid. Meetings are toxic.",
        retailers: shops("Rework Jason Fried", "$28.00", "$26.04"),
        width: 1.85,
        height: 2.85,
        spineThickness: 0.3
    },
    {
        id: "innovators-dilemma",
        title: "The Innovator's Dilemma",
        author: "Clayton Christensen",
        spineColor: "#3f444a",
        spineTextColor: "#f3ecd8",
        coverColor: "#3f444a",
        coverTextColor: "#f3ecd8",
        synopsis:
            "When new technologies cause great firms to fail. The seminal theory of disruptive innovation.",
        excerpt:
            "The processes key to the success of established companies are the very ones that reject disruption.",
        retailers: shops("The Innovators Dilemma Clayton Christensen", "$19.99", "$23.25"),
        width: 1.95,
        height: 3.1,
        spineThickness: 0.4
    },
    {
        id: "start-with-why",
        title: "Start with Why",
        author: "Simon Sinek",
        spineColor: "#7a3a2a",
        spineTextColor: "#f3ecd8",
        coverColor: "#7a3a2a",
        coverTextColor: "#f3ecd8",
        synopsis:
            "How great leaders inspire everyone to take action. The Golden Circle, start with why, then how, then what.",
        excerpt: "People don't buy what you do; they buy why you do it.",
        retailers: shops("Start with Why Simon Sinek", "$18.00", "$16.74"),
        width: 1.95,
        height: 3.0,
        spineThickness: 0.36
    },
    {
        id: "built-to-last",
        title: "Built to Last",
        author: "Jim Collins",
        spineColor: "#3a1818",
        spineTextColor: "#cf9b3a",
        coverColor: "#3a1818",
        coverTextColor: "#cf9b3a",
        synopsis:
            "Successful habits of visionary companies. What makes a company endure for a century, not a quarter.",
        excerpt: "Preserve the core, stimulate progress.",
        retailers: shops("Built to Last Jim Collins", "$19.99", "$22.32"),
        width: 2.0,
        height: 3.15,
        spineThickness: 0.48
    }
]

export const INITIAL_INDEX = Math.floor(BOOKS.length / 2)
